import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  RotateCcw,
  MessageSquare
} from 'lucide-react';
import DocumentVerificationActionModal from './DocumentVerificationActionModal';

interface ProfileVerificationActionsProps {
  verification: {
    id: string;
    status: string;
    username: string;
    reviewed_at?: string;
    reviewer_username?: string;
    rejection_reason?: string;
  };
  onAction: (action: 'approve' | 'reject' | 'reset', id: string, reason?: string) => Promise<boolean>;
  isProcessing: boolean;
}

const ProfileVerificationActions: React.FC<ProfileVerificationActionsProps> = ({
  verification,
  onAction,
  isProcessing
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'approve' | 'reject' | 'reset' | null>(null);

  const getStatusConfig = () => {
    const configs = {
      waiting: { 
        label: 'Aguardando Análise', 
        color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
        icon: Clock 
      },
      pending: { 
        label: 'Documentos Incompletos', 
        color: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
        icon: AlertCircle 
      },
      approved: { 
        label: 'Aprovado', 
        color: 'bg-green-500/20 text-green-300 border-green-500/30',
        icon: CheckCircle 
      },
      rejected: { 
        label: 'Rejeitado', 
        color: 'bg-red-500/20 text-red-300 border-red-500/30',
        icon: XCircle 
      }
    };
    return configs[verification.status as keyof typeof configs] || configs.pending;
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const handleActionClick = (action: 'approve' | 'reject' | 'reset') => {
    setSelectedAction(action);
    setShowModal(true);
  };

  const handleConfirmAction = async (reason?: string) => {
    if (!selectedAction) return;
    
    const success = await onAction(selectedAction, verification.id, reason);
    if (success) {
      setShowModal(false);
      setSelectedAction(null);
    }
  };

  const getAvailableActions = () => {
    const actions = [];
    
    if (verification.status !== 'approved') {
      actions.push({
        type: 'approve' as const,
        label: verification.status === 'rejected' ? 'Reverter p/ Aprovado' : 'Aprovar Perfil',
        icon: CheckCircle,
        variant: 'success',
        description: 'Aprovar verificação do perfil'
      });
    }

    if (verification.status !== 'rejected') {
      actions.push({
        type: 'reject' as const,
        label: verification.status === 'approved' ? 'Reverter p/ Rejeitado' : 'Rejeitar Perfil',
        icon: XCircle,
        variant: 'destructive',
        description: 'Rejeitar verificação com motivo'
      });
    }

    if (verification.status !== 'pending') {
      actions.push({
        type: 'reset' as const,
        label: 'Resetar',
        icon: RotateCcw,
        variant: 'outline',
        description: 'Voltar status para pendente'
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  return (
    <div className="space-y-3 border-t border-white/10 pt-4">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <Badge className={statusConfig.color}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {statusConfig.label}
        </Badge>
        
        {verification.reviewed_at && (
          <div className="text-xs text-white/60">
            {verification.status === 'approved' && '✅ '}
            {verification.status === 'rejected' && '❌ '}
            Por {verification.reviewer_username} em{' '}
            {new Date(verification.reviewed_at).toLocaleDateString('pt-BR')}
          </div>
        )}
      </div>

      {/* Rejection Reason */}
      {verification.status === 'rejected' && verification.rejection_reason && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-red-400 font-medium">Motivo da Rejeição:</p>
              <p className="text-sm text-white/80 mt-1">{verification.rejection_reason}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {availableActions.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {availableActions.map((action) => {
            const Icon = action.icon;
            
            return (
              <Button
                key={action.type}
                onClick={() => handleActionClick(action.type)}
                disabled={isProcessing}
                size="sm"
                variant={action.variant as any}
                className={`text-xs ${
                  action.variant === 'success' 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : action.variant === 'destructive'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white border-white/20'
                }`}
              >
                {isProcessing ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                ) : (
                  <Icon className="h-3 w-3 mr-1" />
                )}
                {action.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Action Modal */}
      <DocumentVerificationActionModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedAction(null);
        }}
        action={selectedAction}
        username={verification.username}
        currentStatus={verification.status}
        onConfirm={handleConfirmAction}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default ProfileVerificationActions;
